import { useEffect, useRef, useState } from "react";
import type { Fee } from "@dealer-center/ddc-pages-fee-manager-interface";
import type { FeeWithUIState } from "../types";

export const useFeeState = (initialFees: Fee[] = []) => {
  const [fees, setFees] = useState<FeeWithUIState[]>(initialFees);
  const prevInitialFeesRef = useRef<Fee[]>([]);

  // Sync with server data while preserving UI state (editing, selected, etc.)
  useEffect(() => {
    // Check if initialFees actually changed (deep comparison by IDs and data)
    const prevIds = prevInitialFeesRef.current
      .map(f => f.id)
      .sort()
      .join(",");
    const currentIds = initialFees
      .map(f => f.id)
      .sort()
      .join(",");
    const prevData = JSON.stringify(
      prevInitialFeesRef.current.map(f => ({
        id: f.id,
        name: f.name,
        amount: f.amount,
        include: f.include
      }))
    );
    const currentData = JSON.stringify(
      initialFees.map(f => ({
        id: f.id,
        name: f.name,
        amount: f.amount,
        include: f.include
      }))
    );

    // Only update if data actually changed
    if (prevIds !== currentIds || prevData !== currentData) {
      prevInitialFeesRef.current = initialFees;

      setFees(prevFees => {
        // If no previous fees, just use the new data
        if (prevFees.length === 0) {
          return initialFees;
        }

        // Create a map of previous fees by ID for quick lookup
        const prevFeesMap = new Map(prevFees.map(f => [f.id, f]));

        // Merge new data with existing UI state
        return initialFees.map(newFee => {
          const prevFee = prevFeesMap.get(newFee.id);
          if (prevFee) {
            // Preserve UI state while updating data
            return {
              ...newFee,
              selected: prevFee.selected,
              editing: prevFee.editing,
              editName: prevFee.editName,
              editAmount: prevFee.editAmount,
              editInclude: prevFee.editInclude
            };
          }
          // New fee that didn't exist before
          return newFee;
        });
      });
    }
  }, [initialFees]);

  const toggleSelection = (feeId: string) => {
    setFees(prevFees =>
      prevFees.map(fee =>
        fee.id === feeId ? { ...fee, selected: !fee.selected } : fee
      )
    );
  };

  const selectAll = () => {
    setFees(prevFees => {
      const anySelected = prevFees.some(f => f.selected);
      return prevFees.map(fee => ({ ...fee, selected: !anySelected }));
    });
  };

  const enterEditMode = (feeId: string) => {
    setFees(prevFees =>
      prevFees.map(fee => {
        if (fee.id === feeId) {
          return {
            ...fee,
            editing: true,
            editName: fee.name,
            editAmount: fee.amount,
            editInclude: fee.include
          };
        }
        // Exit edit mode for all other fees
        return {
          ...fee,
          editing: false,
          editName: undefined,
          editAmount: undefined,
          editInclude: undefined
        };
      })
    );
  };

  const updateEditValues = (
    feeId: string,
    updates: { editName?: string; editAmount?: number | null }
  ) => {
    setFees(prevFees =>
      prevFees.map(fee => (fee.id === feeId ? { ...fee, ...updates } : fee))
    );
  };

  const exitEditMode = (feeId: string) => {
    setFees(prevFees =>
      prevFees.map(fee =>
        fee.id === feeId
          ? {
              ...fee,
              editing: false,
              editName: undefined,
              editAmount: undefined,
              editInclude: undefined
            }
          : fee
      )
    );
  };

  const getSelectedFees = () => fees.filter(f => f.selected);

  const clearSelection = () => {
    setFees(prevFees => prevFees.map(fee => ({ ...fee, selected: false })));
  };

  const isAnyFeeBeingEdited = () => fees.some(f => f.editing);

  const applyOptimisticUpdate = (
    feeId: string,
    updates: { name?: string; amount?: number | null; include?: boolean }
  ) => {
    setFees(prevFees =>
      prevFees.map(fee =>
        fee.id === feeId
          ? {
              ...fee,
              ...updates,
              editing: false,
              editName: undefined,
              editAmount: undefined,
              editInclude: undefined
            }
          : fee
      )
    );
  };

  return {
    fees,
    setFees,
    toggleSelection,
    selectAll,
    enterEditMode,
    updateEditValues,
    exitEditMode,
    getSelectedFees,
    clearSelection,
    isAnyFeeBeingEdited,
    applyOptimisticUpdate
  };
};
